import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '../service/auth.service';
import swal from 'sweetalert2';
import { UserService } from '../service/user.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { DesignService } from '../service/design.service';
import { NavigationService } from '../service/navigation.service';
import { environment } from 'src/environments/environment';
import { InstanceService } from '../service/instance.service';
import { SocialAuthService, SocialUser, GoogleLoginProvider } from 'angularx-social-login';

declare var grecaptcha: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  loginForm: FormGroup
  submitted: boolean = false
  invalidLogin: boolean = false
  returnUrl: string = ""
  autoLogin: boolean = false
  showPassword: boolean = false;
  errorMessage: string | null = null;
  googleUser: SocialUser | null = null;
  showGoogleLogin: boolean = false;

  constructor(
    private formBuilder: FormBuilder, 
    private router: Router, 
    private authService: AuthenticationService,
    private route: ActivatedRoute,
    private userService: UserService,
    private spinner: NgxSpinnerService,
    private designService: DesignService,
    private instanceService: InstanceService,
    private navigationService: NavigationService,
    private socialAuthService: SocialAuthService) { }

  get form() { return this.loginForm.controls; }

  get logoUrl(): string {
    return this.designService.getLogoUrl()
  }

  @HostListener('window:beforeunload', ['$event']) 
  registerPageLeaving($event) {
    this.registerNavigation("LeavePage")
    $event.preventDefault();
  }

  ngOnInit() {
    this.registerNavigation("EnterPage")

    this.loginForm = this.formBuilder.group({
      user: ['', Validators.required],
      password: ['', Validators.required]
    });

    try {
      const loginGroupRoot = this.designService.getSettings("login");
      if (loginGroupRoot && Array.isArray(loginGroupRoot.options)) {
        const opt = loginGroupRoot.options.find(o => (o.key || '').toLowerCase() === 'showgooglelogin');
        this.showGoogleLogin = !!(opt && typeof opt.value === 'string' && opt.value.toLowerCase() === 'true');
      } else {
        this.showGoogleLogin = false;
      }
    } catch {
      this.showGoogleLogin = false;
    }

    this.socialAuthService.authState.subscribe((user: SocialUser) => {
      this.googleUser = user;
    });

    this.route.queryParams
      .subscribe(params => {
        this.returnUrl = params['return'] || 'dashboard';

        let code = params['code'];

        if(code) {
          let login = JSON.parse(atob(code))
          this.autoLogin = true;
          this.performLogin(login.user, login.password)
        } else {
          // swal.close()
          this.spinner.hide();
        }
      });
  }

  private registerNavigation(actionName: string) {
    let tenantId = localStorage.getItem("tid")
    let workspaceId = localStorage.getItem("wid")

    if(tenantId && workspaceId) {
      this.navigationService
        .postWithComplement(
          `?tenant-id=${tenantId}&company-id=${workspaceId}&env=${environment.name}`, 
          { action: actionName, url: location.href })
        .subscribe(
          () => console.log("navigation registered"),
          ()=> console.log("error on registering navigation"))
    }
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      return;
    }

    this.spinner.show();

    grecaptcha.enterprise.ready(() => {
      try {
        grecaptcha.enterprise.execute('6LdlJlEpAAAAAKeynkrAqzna1NIeGowqHx3tZc-w', {action: 'LOGIN'}).then(token => {
          this.performLogin(this.form.user.value, this.form.password.value, token);
        }).catch(error => {
          this.performLogin(this.form.user.value, this.form.password.value);
        }); 
      } catch (error) {
        this.performLogin(this.form.user.value, this.form.password.value);
      }
    });
  }

  performLogin(userName: string, password: string, captchaToken?: string) {
    this.submitted = true
    this.invalidLogin = false
    this.errorMessage = null

    this.authService.login(userName, password, captchaToken)
      .subscribe(successed => {
        if (successed) {
          this.userService.loadUserAttributes()
            .subscribe(() => {
              this.loadDesign()
              this.sendLoginAction()
            }, () => {
              this.authService.logout()
              swal.fire('Atenção', 'Problema ao realizar o login. Por favor, tente novamente.', 'error')
            })
        } else {
          this.invalidLogin = true;
          this.errorMessage = 'Usuário ou senha inválidos.'
        }

        this.spinner.hide();
      },
      err => {
        this.authService.logout()
        this.spinner.hide();
        this.invalidLogin = true;
        this.errorMessage = this.buildLoginErrorMessage(err);
      });
  }

  private buildLoginErrorMessage(err: any): string {
    const isOffline = typeof navigator !== 'undefined' && navigator && (navigator as any).onLine === false;
    if (isOffline) {
      return 'Sem conexão com a internet. Verifique sua rede e tente novamente.';
    }

    const status = err?.status;
    const statusText = err?.statusText || '';
    const payload = err?.error;

    const hasContent = (() => {
      if (payload === null || payload === undefined) return false;
      if (typeof payload === 'string') return payload.trim().length > 0;
      if (payload instanceof Blob) return true;
      if (typeof payload === 'object') return Object.keys(payload).length > 0;
      return false;
    })();

    const extractMessageFromPayload = (content: any): string | null => {
      if (content === null || content === undefined) return null;
      if (typeof content === 'string') return content.trim();
      if (typeof content === 'object') {
        const candidates = [
          content.message,
          content.error,
          content.detail,
          content.description,
          content.title,
          content.msg,
        ];
        for (const candidate of candidates) {
          if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
        }
        // Arrays of errors { errors: [ { message: '...' } ] }
        if (Array.isArray((content as any).errors) && (content as any).errors.length > 0) {
          const first = (content as any).errors[0];
          if (typeof first === 'string') return first;
          if (first && typeof first.message === 'string') return first.message;
        }
        try {
          return JSON.stringify(content);
        } catch {
          return null;
        }
      }
      return null;
    };

    // 403 sem conteúdo => mensagem padrão
    if (status === 403) {
      if (!hasContent) {
        return 'Usuário ou senha inválidos.';
      }
      const fromPayload = extractMessageFromPayload(payload);
      return fromPayload || 'Acesso negado (403).';
    }

    // Falta de conexão (status 0)
    if (status === 0) {
      return 'Sem conexão com a internet. Verifique sua rede e tente novamente.';
    }

    // Serviço indisponível 502/503
    if (status === 502 || status === 503) {
      return `Serviço indisponível (${status}). Tente novamente mais tarde.`;
    }

    // Se houver conteúdo na resposta, exibimos
    if (hasContent) {
      const fromPayload = extractMessageFromPayload(payload);
      if (fromPayload) return fromPayload;
    }

    // Fallback genérico com status
    if (status) {
      return `Falha no login. (${status} ${statusText}).`;
    }
    return 'Falha no login. Tente novamente mais tarde.';
  }

  private loadDesign() {
    this.designService.loadDesignPolicy()
      .subscribe((policy) => {
        if(this.userService.hasValidPassword()) {
          this.router.navigateByUrl(this.returnUrl)
        } else {
          this.router.navigate(['create-password'], {
            queryParams: {
                return: this.returnUrl
            }
          })
        }
      })
  }

  private sendLoginAction() {
    let userAttributes = this.userService.getCurrentUserInstanceReferences();

    userAttributes.forEach(attr => {
      this.instanceService
        .executeById(attr.value, [
          {
            key: "login completed",
            type: "ActionEvent"
          }
        ])
        .subscribe(() => {},
                  err => { console.log("error in login action event.") })
    });
  }

  formHasErrors() {
    return !this.autoLogin && this.loginForm.invalid
  }
 
  passwordHasErrors() {
    return !this.autoLogin && this.form.password.errors && this.form.password.errors.required
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  userHasErrors() {
    return !this.autoLogin && this.form.user.errors && this.form.user.errors.required
  }

  performGoogleLogin() {
    this.spinner.show();
    this.invalidLogin = false;
    this.errorMessage = null;

    this.socialAuthService.signIn(GoogleLoginProvider.PROVIDER_ID)
      .then((user: SocialUser) => {
        const idToken = user && user.idToken ? user.idToken : (this.googleUser ? this.googleUser.idToken : null);
        if (!idToken) {
          this.spinner.hide();
          swal.fire('Atenção', 'Não foi possível obter o token do Google.', 'error');
          return;
        }

        this.authService.loginGoogle(idToken)
          .subscribe(successed => {
            if (successed) {
              this.userService.loadUserAttributes()
                .subscribe(() => {
                  this.loadDesign()
                  this.sendLoginAction()
                }, () => {
                  this.authService.logout()
                  swal.fire('Atenção', 'Problema ao realizar o login. Por favor, tente novamente.', 'error')
                })
            } else {
              this.invalidLogin = true;
              this.errorMessage = 'Falha na autenticação com o Google.';
            }

            this.spinner.hide();
          }, err => {
            this.authService.logout();
            this.spinner.hide();
            this.invalidLogin = true;
            this.errorMessage = this.buildLoginErrorMessage(err) || 'Falha na autenticação com o Google.';
          });
      })
      .catch(() => {
        this.spinner.hide();
        swal.fire('Atenção', 'Erro ao tentar autenticar com o Google.', 'error');
      });
  }
}
